import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { streamTrafikQuote } from '../services/onlineTrafikService';
import Footer from '../components/Footer';
import OnlineTrafikSigortasi1 from './OnlineTrafikSigortasi1';
import OnlineTrafikSigortasi2 from './OnlineTrafikSigortasi2';
import OnlineTrafikSigortasi3 from './OnlineTrafikSigortasi3';

// Sigo'nun adim bazli konusmalari
const sigoMessages = {
  2: {
    greeting: 'Merhaba! Ben SiGo, yapay zeka destekli sigorta asistanınız.',
    subtitle: '10+ sigorta şirketini saniyeler içinde tarayıp, bütçenize en uygun trafik sigortasını bulacağım. Hadi başlayalım!'
  },
  3: {
    greeting: 'Harika, bilgilerinizi aldım!',
    subtitle: 'Şimdi aracınızın plakasına ihtiyacım var. Plakayı girin, gerisini bana bırakın.'
  },
  4: {
    greeting: 'Neredeyse hazırız!',
    subtitle: 'Son olarak ruhsat bilgilerinizi girin. Ardından tüm şirketleri sizin için tarayacağım.'
  },
  5: {
    loading: 'Yapay zeka motorum çalışıyor! Tüm sigorta şirketlerini sizin için tarıyorum...',
    done: 'Fiyat Sorgusu tamamlandı! İşte bütçenize en uygun teklifler. En düşük fiyatı sizin için en üste koydum.'
  }
};

const OnlineTrafikSigortasi = () => {
  // step 1=intro, 2=TC form, 3=plaka, 4=ruhsat, 5=sonuclar
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    tcNo: '', phone: '', birthDate: '',
    plate: '', belgeSeri: '', belgeNo: ''
  });

  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [systemMessage, setSystemMessage] = useState('');
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: null, tc: null, vehicle: null });
  const [totalCompanies, setTotalCompanies] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [hasActivePolicy, setHasActivePolicy] = useState(false);
  const [policyEndDate, setPolicyEndDate] = useState(null);

  // Sigo chat animasyonu
  const [showBubble, setShowBubble] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [typedSubtitle, setTypedSubtitle] = useState('');

  const quotesRef = useRef([]);
  const cancelRef = useRef(null);

  // Sigo yazi yazma efekti
  useEffect(() => {
    if (step === 1) return; // intro sayfasinda typing yok

    setShowBubble(false);
    setTypedText('');
    setTypedSubtitle('');

    const bubbleTimer = setTimeout(() => setShowBubble(true), 300);

    let msg = '';
    if (step === 5) {
      msg = loading ? sigoMessages[5].loading : sigoMessages[5].done;
    } else {
      msg = sigoMessages[step]?.greeting || '';
    }

    const sub = step >= 2 && step <= 4 ? (sigoMessages[step]?.subtitle || '') : '';

    let i = 0;
    const typeTimer = setInterval(() => {
      if (i <= msg.length) {
        setTypedText(msg.slice(0, i));
        i++;
      } else {
        clearInterval(typeTimer);
        if (sub) {
          let j = 0;
          const subTimer = setInterval(() => {
            if (j <= sub.length) {
              setTypedSubtitle(sub.slice(0, j));
              j++;
            } else {
              clearInterval(subTimer);
            }
          }, 15);
        }
      }
    }, 20);

    return () => {
      clearTimeout(bubbleTimer);
      clearInterval(typeTimer);
    };
  }, [step, loading, completed]);

  // ─── INPUT HANDLERS ───
  const handleTcChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 11);
    setFormData(prev => ({ ...prev, tcNo: value }));
    if (error) setError('');
  };

  const handlePhoneChange = (e) => {
    let raw = e.target.value.replace(/\D/g, '');
    raw = raw.replace(/^0+/, '');
    raw = raw.slice(0, 10);
    setFormData(prev => ({ ...prev, phone: raw }));
    if (error) setError('');
  };

  const handlePlateChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, plate: value }));
    if (error) setError('');
  };

  const handleBelgeSeriChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    setFormData(prev => ({ ...prev, belgeSeri: value }));
    if (error) setError('');
  };

  const handleBelgeNoChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
    setFormData(prev => ({ ...prev, belgeNo: value }));
    if (error) setError('');
  };

  const formatPhone = (val) => {
    if (!val) return '';
    if (val.length <= 3) return val;
    if (val.length <= 6) return `${val.slice(0, 3)} ${val.slice(3)}`;
    return `${val.slice(0, 3)} ${val.slice(3, 6)} ${val.slice(6)}`;
  };

  // ─── DOGUM TARIHI ───
  const handleBirthDateChange = (e) => {
    const input = e.target.value;
    const prevVal = formData.birthDate;
    let raw = input.replace(/[^\d/]/g, '');

    if (raw.length < prevVal.length) {
      if (prevVal.endsWith('/') && !raw.endsWith('/')) {
        raw = raw.slice(0, -1);
      }
      setFormData(prev => ({ ...prev, birthDate: raw }));
      if (error) setError('');
      return;
    }

    const digits = raw.replace(/\D/g, '').slice(0, 8);
    let formatted = '';
    for (let i = 0; i < digits.length; i++) {
      if (i === 2 || i === 4) formatted += '/';
      formatted += digits[i];
    }

    setFormData(prev => ({ ...prev, birthDate: formatted }));
    if (error) setError('');
  };

  const validateBirthDate = (val) => {
    if (!val || val.length !== 10) return false;
    const parts = val.split('/');
    if (parts.length !== 3) return false;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (day < 1 || day > 31) return false;
    if (month < 1 || month > 12) return false;
    if (year < 1920 || year > 2010) return false;
    const date = new Date(year, month - 1, day);
    return date.getDate() === day && date.getMonth() === month - 1;
  };

  const birthDateToISO = (val) => {
    if (!val || val.length !== 10) return '';
    const parts = val.split('/');
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  // ─── STEP HANDLERS ───
  const handleStep1 = (e) => {
    e.preventDefault();
    if (formData.tcNo.length !== 11) { setError('TC Kimlik No 11 haneli olmalıdır'); return; }
    if (!validateBirthDate(formData.birthDate)) { setError('Geçerli bir doğum tarihi giriniz (GG/AA/YYYY)'); return; }
    if (formData.phone.length < 10) { setError('Geçerli bir telefon numarası giriniz'); return; }
    setError('');
    setStep(3);
  };

  const handleStep2 = (e) => {
    e.preventDefault();
    if (!formData.plate || formData.plate.length < 5) { setError('Geçerli bir plaka giriniz'); return; }
    setError('');
    setStep(4);
  };

  const handleStep3 = (e) => {
    e.preventDefault();
    if (!formData.belgeSeri) { setError('Belge seri harflerini giriniz'); return; }
    if (!formData.belgeNo || formData.belgeNo.length < 4) { setError('Belge numarasını giriniz'); return; }
    setError('');
    startQuery();
  };

  const handleGoBack = (toStep) => {
    setStep(toStep);
    setError('');
  };

  // ─── API SORGU ───
  const startQuery = () => {
    setLoading(true);
    setQuotes([]);
    setSystemMessage('');
    setCompleted(false);
    setCustomerInfo({ name: null, tc: null, vehicle: null });
    setTotalCompanies(null);
    setPdfUrl(null);
    setHasActivePolicy(false);
    setPolicyEndDate(null);
    quotesRef.current = [];
    setStep(5);

    if (cancelRef.current) cancelRef.current();

    const registrySerialNo = formData.belgeSeri + formData.belgeNo;

    const queryData = {
      tcNo: formData.tcNo,
      plate: formData.plate,
      registrySerialNo: registrySerialNo,
      birthDate: birthDateToISO(formData.birthDate),
      phone: formData.phone
    };

    cancelRef.current = streamTrafikQuote(
      queryData,
      (data) => {
        if (data.customerName) setCustomerInfo(prev => ({ ...prev, name: data.customerName }));
        if (data.customerTC) setCustomerInfo(prev => ({ ...prev, tc: data.customerTC }));
        if (data.vehicleInfo) setCustomerInfo(prev => ({ ...prev, vehicle: data.vehicleInfo }));
        if (data.totalCompanies) setTotalCompanies(data.totalCompanies);
        if (data.pdfUrl) setPdfUrl(data.pdfUrl);
        if (data.hasActivePolicy) setHasActivePolicy(true);
        if (data.policyEndDate) setPolicyEndDate(data.policyEndDate);

        if (!data.quote) return;
        if (data.quote.companyCode === 'SYSTEM') {
          setSystemMessage(data.quote.message);
          return;
        }

        setQuotes(prev => {
          const existing = prev.findIndex(q => q.companyCode === data.quote.companyCode);
          let newQuotes;
          if (existing >= 0) {
            newQuotes = [...prev];
            newQuotes[existing] = data.quote;
          } else {
            newQuotes = [...prev, data.quote];
          }
          quotesRef.current = newQuotes;
          return newQuotes;
        });
      },
      (err) => {
        setError(typeof err === 'string' ? err : err.message || 'Bir hata oluştu');
        setLoading(false);
      },
      () => {
        setLoading(false);
        setCompleted(true);
      }
    );
  };

  const handleNewQuery = () => {
    setStep(1);
    setFormData({ tcNo: '', phone: '', birthDate: '', plate: '', belgeSeri: '', belgeNo: '' });
    setQuotes([]);
    setError('');
    setCompleted(false);
    setCustomerInfo({ name: null, tc: null, vehicle: null });
    setSystemMessage('');
    setHasActivePolicy(false);
    setPolicyEndDate(null);
    setPdfUrl(null);
    setTotalCompanies(null);
  };

  const formatPrice = (price) => {
    if (!price) return null;
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency', currency: 'TRY',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(price);
  };

  // ─── RENDER ───
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/20 to-sky-50/20">
      <Helmet>
        <title>Online Trafik Sigortası Fiyat Sorgulama | SigortamOlsun</title>
        <meta name="description" content="Online trafik sigortası fiyat sorgulama. 20+ sigorta şirketinden anında teklif alın, karşılaştırın ve en uygun fiyatı bulun." />
        <link rel="canonical" href="https://sigortamolsun.com/online-trafik-sigortasi" />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 py-6">

        {/* Step 1: Sigo Intro */}
        {step === 1 && (
          <OnlineTrafikSigortasi1 onStart={() => setStep(2)} />
        )}

        {/* Step 2-4: Form Adimlari */}
        {(step >= 2 && step <= 4) && (
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-[70vh]">
              {/* Sol: Sigo Maskot - step 1 ile aynı */}
              <div className="flex flex-col items-center">
                <div className="w-52 h-52 sm:w-64 sm:h-64 lg:w-72 lg:h-72 animate-sigo-float">
                  <img src="/sigo2.png" alt="Sigo" className="w-full h-full object-contain drop-shadow-2xl" />
                </div>
              </div>

              {/* Sag: Form */}
              <div className="lg:-ml-16">
                <OnlineTrafikSigortasi2
                  step={step} formData={formData} error={error}
                  showBubble={showBubble} typedText={typedText} typedSubtitle={typedSubtitle}
                  onTcChange={handleTcChange} onPhoneChange={handlePhoneChange}
                  onBirthDateChange={handleBirthDateChange} onPlateChange={handlePlateChange}
                  onBelgeSeriChange={handleBelgeSeriChange} onBelgeNoChange={handleBelgeNoChange}
                  onStep1Submit={handleStep1} onStep2Submit={handleStep2} onStep3Submit={handleStep3}
                  onGoBack={handleGoBack} formatPhone={formatPhone}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Sonuclar */}
        {step === 5 && (
          <div className="max-w-2xl mx-auto">
            <OnlineTrafikSigortasi3
              loading={loading} completed={completed} quotes={quotes} error={error}
              customerInfo={customerInfo} formData={formData} totalCompanies={totalCompanies}
              hasActivePolicy={hasActivePolicy} policyEndDate={policyEndDate}
              systemMessage={systemMessage} pdfUrl={pdfUrl}
              typedText={typedText} formatPrice={formatPrice} onNewQuery={handleNewQuery}
            />
          </div>
        )}

      </div>

      <Footer />
    </div>
  );
};

export default OnlineTrafikSigortasi;
